import { Button, Tabs } from "@caprikorn/dynamo-ui";
import { useIsFetching } from "@tanstack/react-query";
import { type FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { DataExportDrawer } from "@components/DataExportDrawer/DataExportDrawer";
import { getPerimeterExported } from "@components/DataExportDrawer/DataExportDrawer.utils";
import { Header } from "@components/Header/Header";
import {
  getActiveTabValue,
  useBuildPerimeterBreadcrumb,
} from "@components/PerimeterHeader/PerimeterHeader.utils";
import { RequestDrawer } from "@components/RequestDrawer/RequestDrawer";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useReferences } from "@contexts/References/References.context";
import { useSelectedPerimeters } from "@contexts/SelectedPerimeters/SelectedPerimeters.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { Download, EditPencil01, LayerTriple, Users } from "@icons";
import { ROUTES } from "@router/router.constants";
import { QUERY_KEYS } from "@services/keys";
import {
  isContractsRoute,
  isCustomersRoute,
  isSiteRoute,
} from "@utils/perimeterRoute.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PerimeterHeader.module.css";
import type { PerimeterHeaderProviderProps } from "./PerimeterHeader.types";

export const PerimeterHeader: FC<PerimeterHeaderProviderProps> = ({
  groupsDropdownOptions,
  groupsOnDropdownChange,
  groupsDropdownValue,
}) => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation("main");
  const { hasTicketing } = useTenant();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isDataExportDrawerOpen, setIsDataExportDrawerOpen] = useState(false);
  const [isRequestDrawerOpen, setIsRequestDrawerOpen] = useState(false);

  const { references } = useReferences();
  const { selectedPerimeters } = useSelectedPerimeters();

  const dataKeyCheckFetching = [QUERY_KEYS.PERIMETER_DATA, currentGroup];
  const contactKeyCheckFetching = [QUERY_KEYS.CONTACT, currentGroup];
  const siteDetailKeyCheckFetching = [QUERY_KEYS.SITE, currentGroup];

  const isDataLoading =
    useIsFetching({
      predicate: (query) =>
        dataKeyCheckFetching.every((elem) => query?.queryKey.includes(elem)),
    }) > 0;
  const isContactLoading =
    useIsFetching({
      predicate: (query) =>
        contactKeyCheckFetching.every((elem) => query?.queryKey.includes(elem)),
    }) > 0;
  const isSiteDetailOverviewLoading =
    useIsFetching({
      predicate: (query) =>
        siteDetailKeyCheckFetching.every((elem) =>
          query?.queryKey.includes(elem),
        ),
    }) > 0;

  const ready = isSiteRoute(pathname)
    ? !isSiteDetailOverviewLoading
    : !isContactLoading && !isDataLoading;

  const breadcrumbPaths = useBuildPerimeterBreadcrumb(pathname, references);

  return (
    <div className={classes.container}>
      <Header
        breadcrumbPaths={breadcrumbPaths}
        dropdownOptions={groupsDropdownOptions}
        dropdownValue={groupsDropdownValue}
        onDropdownChange={(select) => groupsOnDropdownChange(select)}
      />
      <div className={classes.header}>
        <Tabs
          hasNoBorder
          onChange={(value) => navigate(value)}
          value={getActiveTabValue(pathname)}
        >
          <Tabs.Tab
            data-testid="TabMenuItem-perimeter-clients"
            icon={LayerTriple}
            label={t("perimeter.header.tabs.clients")}
            value={ROUTES.PERIMETER_CUSTOMERS}
          />
          <Tabs.Tab
            data-testid="TabMenuItem-perimeter-regroupments"
            icon={LayerTriple}
            label={t("perimeter.header.tabs.contracts")}
            value={ROUTES.PERIMETER_CONTRACTS}
          />
          <Tabs.Tab
            data-testid="TabMenuItem-perimeter-sites"
            icon={Users}
            label={t("perimeter.header.tabs.sites")}
            value={ROUTES.PERIMETER_SITES}
          />
        </Tabs>

        <div className={classes.buttons}>
          {(isCustomersRoute(pathname) || isContractsRoute(pathname)) && (
            <Button
              data-testid="PerimeterHeader-download"
              disabled={selectedPerimeters.length === 0}
              onClick={() => setIsDataExportDrawerOpen(true)}
              startIcon={Download}
              variant="primary"
            >
              {t("perimeter.header.billDownloadButton")}
            </Button>
          )}
          {hasTicketing && (
            <Button
              data-testid="PerimeterHeader-request"
              disabled={!ready}
              onClick={() => setIsRequestDrawerOpen(true)}
              startIcon={EditPencil01}
              variant="secondary"
            >
              {t("perimeter.header.askRequestButton")}
            </Button>
          )}
        </div>
      </div>

      {isDataExportDrawerOpen && (
        <DataExportDrawer
          onHide={() => setIsDataExportDrawerOpen(false)}
          perimeterExported={getPerimeterExported(selectedPerimeters)}
        />
      )}

      {isRequestDrawerOpen && (
        <RequestDrawer onHide={() => setIsRequestDrawerOpen(false)} />
      )}
    </div>
  );
};
